'use client'

import RichText from '@/components/RichText'
import { bgClass } from '@/utilities/bgClass'
import type { Media, PressGridBlock as PressGridProps } from '@/payload-types'
import { ReactElement, JSXElementConstructor, ReactNode, ReactPortal } from 'react'

export const PressGridBlock = (props: PressGridProps & { id?: string }) => {
  const { id, introContent, items = [], background = 'none' } = props

  if (!items.length) return null

  return (
    <section
      id={id ? `block-${id}` : undefined}
      className={`${bgClass(background)} py-12 md:py-16`}
    >
      <div className="container space-y-10">
        {introContent ? (
          <RichText data={introContent} enableGutter={false} className="[&_p]:max-w-3xl" />
        ) : null}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, index) => {
            const key = item?.id ?? `${item?.title ?? 'press'}-${index}`
            const media = (item?.image as Media) || undefined

            return (
              <a
                key={key}
                href={item?.url ?? '#'}
                target="_blank"
                rel="noreferrer"
                className="group relative block aspect-square overflow-hidden rounded-3xl border border-neutral-200 bg-neutral-100 shadow-sm transition hover:-translate-y-1 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600 dark:border-neutral-700 dark:bg-neutral-800"
              >
                {media?.url ? (
                  <img
                    src={media.url}
                    alt={media.alt || item?.title || 'Press feature'}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-emerald-700" />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black transition group-hover:to-black/90" />
                <div className="absolute inset-x-0 bottom-0 space-y-2 px-5 pb-6 pt-8 text-white">
                  {item?.source ? (
                    <p className="text-sm font-semibold uppercase tracking-[0.28em] opacity-80 text-minigreen-1">
                      {item.source}
                    </p>
                  ) : null}
                  {item?.title ? (
                    <p className="text-lg font-semibold leading-snug text-white line-clamp-3">
                      {item.title}
                    </p>
                  ) : null}
                </div>
              </a>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default PressGridBlock
